/**
 * [INPUT]: 依赖 ./types 的 Landmark
 * [OUTPUT]: 对外提供 2D 几何工具：distance / midpoint / angleDeg / vertexAngleDeg / mirrorX / rotateAround / clamp01
 * [POS]: apps/web/src/scorer 的几何数学底座，被 geo-features / landmarks-map / confidence 消费
 * [PROTOCOL]: 变更时更新此头部，然后检查 ../CLAUDE.md
 */
import type { Landmark } from './types'

export function distance(a: Landmark, b: Landmark): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

/** 两点中点（仅 x/y，2D 语义）。 */
export function midpoint(a: Landmark, b: Landmark): Landmark {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
}

/** b 相对 a 的方向角（度），x 轴正方向为 0，逆时针为正。 */
export function angleDeg(a: Landmark, b: Landmark): number {
  return (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI
}

/** 在顶点 v 处，由 v→a 与 v→b 张成的夹角（度），范围 0..180。 */
export function vertexAngleDeg(a: Landmark, v: Landmark, b: Landmark): number {
  const v1x = a.x - v.x
  const v1y = a.y - v.y
  const v2x = b.x - v.x
  const v2y = b.y - v.y
  const dot = v1x * v2x + v1y * v2y
  const m1 = Math.hypot(v1x, v1y)
  const m2 = Math.hypot(v2x, v2y)
  const cos = dot / Math.max(m1 * m2, 1e-9)
  return (Math.acos(Math.max(-1, Math.min(1, cos))) * 180) / Math.PI
}

/** 关于竖直线 x = axisX 的镜像点。 */
export function mirrorX(p: Landmark, axisX: number): Landmark {
  return { x: 2 * axisX - p.x, y: p.y }
}

/**
 * 将点 p 绕 center 旋转 angleDeg 度（2D，仅 x/y）。
 * 图像坐标系 y 向下，矩阵 [cos -sin; sin cos]：angleDeg>0 视觉顺时针（与 rollFromLandmarks 同向）。
 */
export function rotateAround(p: Landmark, angleDeg: number, center: Landmark): Landmark {
  const r = (angleDeg * Math.PI) / 180
  const cos = Math.cos(r)
  const sin = Math.sin(r)
  const dx = p.x - center.x
  const dy = p.y - center.y
  return { x: center.x + dx * cos - dy * sin, y: center.y + dx * sin + dy * cos }
}

export function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v))
}

export function mean(xs: number[]): number {
  if (xs.length === 0) return 0
  return xs.reduce((s, x) => s + x, 0) / xs.length
}